/**
 * Google Analytics Utility
 *
 * Provides helper functions for tracking events and page views.
 * Uses the gtag API loaded in index.html.
 */

// GA Measurement ID
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-QT04SVWBRC';

/**
 * Check if gtag is available
 */
const isGtagAvailable = () => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

/**
 * Track a page view
 * @param {string} path - The page path (e.g., '/app', '/pricing')
 * @param {string} title - The page title
 */
export const trackPageView = (path, title) => {
  if (!isGtagAvailable()) return;

  window.gtag('config', GA_MEASUREMENT_ID, {
    page_path: path,
    page_title: title,
  });
};

/**
 * Track a custom event
 * @param {string} eventName - The event name (e.g., 'sign_up', 'purchase')
 * @param {object} params - Additional event parameters
 */
export const trackEvent = (eventName, params = {}) => {
  if (!isGtagAvailable()) return;

  window.gtag('event', eventName, params);
};

// ============================================
// Pre-defined Event Trackers
// ============================================

/**
 * Track user sign up
 * @param {string} method - Sign up method (e.g., 'email', 'google')
 */
export const trackSignUp = (method = 'auth0') => {
  trackEvent('sign_up', { method });
};

/**
 * Track user login
 * @param {string} method - Login method
 */
export const trackLogin = (method = 'auth0') => {
  trackEvent('login', { method });
};

/**
 * Track subscription purchase
 * @param {string} tier - Subscription tier (essentials, excellence, mastery, ultimate)
 * @param {string} billingPeriod - Billing period (monthly, 3-month, 6-month)
 * @param {number} value - Price value
 */
export const trackPurchase = (tier, billingPeriod, value) => {
  trackEvent('purchase', {
    currency: 'USD',
    value,
    items: [{
      item_id: `${tier}_${billingPeriod}`,
      item_name: `${tier} - ${billingPeriod}`,
      item_category: 'subscription',
      price: value,
    }],
  });
};

/**
 * Track exam started
 * @param {string} examType - Type of exam (practice, adaptive, full)
 * @param {string} examId - Exam identifier
 */
export const trackExamStart = (examType, examId) => {
  trackEvent('exam_start', {
    exam_type: examType,
    exam_id: examId,
  });
};

/**
 * Track exam completed
 * @param {string} examType - Type of exam
 * @param {string} examId - Exam identifier
 * @param {number} score - Score percentage (0-100)
 * @param {number} duration - Duration in seconds
 */
export const trackExamComplete = (examType, examId, score, duration) => {
  trackEvent('exam_complete', {
    exam_type: examType,
    exam_id: examId,
    score,
    duration,
  });
};

/**
 * Track flashcard session
 * @param {string} subject - Flashcard subject
 * @param {number} cardsReviewed - Number of cards reviewed
 * @param {number} duration - Duration in seconds
 */
export const trackFlashcardSession = (subject, cardsReviewed, duration) => {
  trackEvent('flashcard_session', {
    subject,
    cards_reviewed: cardsReviewed,
    duration,
  });
};

/**
 * Track quiz completed
 * @param {string} quizId - Quiz identifier
 * @param {number} score - Score percentage
 */
export const trackQuizComplete = (quizId, score) => {
  trackEvent('quiz_complete', {
    quiz_id: quizId,
    score,
  });
};

/**
 * Track feature usage
 * @param {string} feature - Feature name (e.g., 'study_plan', 'calendar', 'lectures')
 */
export const trackFeatureUse = (feature) => {
  trackEvent('feature_use', {
    feature_name: feature,
  });
};

/**
 * Track button click
 * @param {string} buttonName - Button identifier
 * @param {string} location - Where the button is located
 */
export const trackButtonClick = (buttonName, location) => {
  trackEvent('button_click', {
    button_name: buttonName,
    location,
  });
};

/**
 * Track errors
 * @param {string} errorType - Type of error
 * @param {string} errorMessage - Error message
 * @param {string} location - Where the error occurred
 */
export const trackError = (errorType, errorMessage, location) => {
  trackEvent('error', {
    error_type: errorType,
    error_message: errorMessage,
    location,
  });
};

export default {
  trackPageView,
  trackEvent,
  trackSignUp,
  trackLogin,
  trackPurchase,
  trackExamStart,
  trackExamComplete,
  trackFlashcardSession,
  trackQuizComplete,
  trackFeatureUse,
  trackButtonClick,
  trackError,
};
